import { useCallback, useEffect, useRef, useState } from "react";
import { deltaSync, ensureSynced, startPolling, manualFullSync } from "../data/syncManager";

// Bootstraps the IndexedDB catalog sync on register open, then starts the 60s delta poll.
export function useRegisterSync() {
  // `tick` bumps after every delta poll so consumers can re-read reference data (category order,
  // visibility, taxes, promos) from IndexedDB without a page refresh.
  const [state, setState] = useState({ ready: false, progress: 0, error: null, tick: 0 });
  const stopRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        await ensureSynced((p) => mounted && setState((s) => ({ ...s, progress: p })));
        if (!mounted) return;
        setState((s) => ({ ...s, ready: true, progress: 100, error: null }));
        stopRef.current = startPolling(
          // eslint-disable-next-line no-console
          (e) => console.warn("[web-register] delta poll error", e),
          () => mounted && setState((s) => ({ ...s, tick: s.tick + 1 }))
        );
      } catch (e) {
        if (mounted) setState((s) => ({ ...s, ready: false, progress: 0, error: e }));
      }
    })();
    return () => {
      mounted = false;
      if (stopRef.current) stopRef.current();
    };
  }, []);

  // On-demand delta pull, for when the register itself just changed the catalog (Quick-Add) and can't
  // wait out the 60s poll. Bumps `tick` on success so reference-data consumers re-read too.
  const refresh = useCallback(async () => {
    await deltaSync();
    setState((s) => ({ ...s, tick: s.tick + 1 }));
  }, []);

  // Android manual Sync: full re-sync, then bump `tick` so categories/taxes/promos re-read.
  const fullResync = useCallback(async () => {
    const r = await manualFullSync((p) => setState((s) => ({ ...s, progress: p })));
    if (r && r.status === "ok") setState((s) => ({ ...s, tick: s.tick + 1 }));
    return r;
  }, []);

  return { ...state, refresh, fullResync };
}
